import { useMemo } from 'react';
import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';

type ExtractedSegment =
  RouterOutputs['translationOverrides']['extractSegments']['segments'][number];
type OverrideRow = RouterOutputs['translationOverrides']['list']['rows'][number];

export type SegmentOverride = Pick<OverrideRow, 'overrideValue' | 'isStale'>;

export type SegmentWithOverride = {
  segment: ExtractedSegment;
  override: SegmentOverride | undefined;
};

// A node absent from the map renders its English content unchanged.
export type CanvasRowsByNode = Map<string, SegmentWithOverride[]>;

const QUERY_STALE_TIME = 30_000;

export function useCanvasTranslation({
  resourceId,
  resourceVersionId,
  language,
  enabled,
}: {
  resourceId: string;
  resourceVersionId: number;
  language: string | null;
  enabled: boolean;
}): {
  isLoading: boolean;
  isError: boolean;
  rowsByNode: CanvasRowsByNode;
} {
  const active = enabled && language != null;

  const segmentsQuery = trpcAdmin.translationOverrides.extractSegments.useQuery(
    { resourceVersionId, language: language ?? '' },
    { enabled: active, staleTime: QUERY_STALE_TIME },
  );
  const overridesQuery = trpcAdmin.translationOverrides.list.useQuery(
    { resourceId, language: language ?? '' },
    { enabled: active, staleTime: QUERY_STALE_TIME },
  );

  const rows = useMemo<SegmentWithOverride[]>(() => {
    const segments = segmentsQuery.data?.segments ?? [];
    const overridesByKey = new Map<string, OverrideRow>(
      (overridesQuery.data?.rows ?? []).map((row) => [row.segmentKey, row]),
    );
    return segments.map((segment) => ({
      segment,
      override: overridesByKey.get(segment.segmentKey),
    }));
  }, [segmentsQuery.data?.segments, overridesQuery.data?.rows]);

  const rowsByNode = useMemo<CanvasRowsByNode>(() => {
    const grouped: CanvasRowsByNode = new Map();
    for (const row of rows) {
      const nodeId = row.segment.nodeId;
      if (nodeId === null) {
        continue;
      }
      const list = grouped.get(nodeId);
      if (list) {
        list.push(row);
      } else {
        grouped.set(nodeId, [row]);
      }
    }
    return grouped;
  }, [rows]);

  return {
    isLoading: active && (segmentsQuery.isLoading || overridesQuery.isLoading),
    isError: active && (segmentsQuery.isError || overridesQuery.isError),
    rowsByNode,
  };
}
